import React, { useState, useEffect } from 'react';
import { Play, CheckCircle, XCircle, Edit3, ArrowRight, RefreshCw, TrendingUp, AlertCircle } from 'lucide-react';

const InteractiveLearning = () => {
  const [people, setPeople] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentPerson, setCurrentPerson] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [actualEvent, setActualEvent] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [algorithmVersion, setAlgorithmVersion] = useState('v7.6.0');
  const [corrections, setCorrections] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

  useEffect(() => {
    loadPeople();
  }, []);

  const loadPeople = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/download/aa-rated-people`);
      const data = await response.json();
      setPeople(data.slice(0, 150)); // All AA-rated people
    } catch (error) {
      console.error('Error loading people:', error);
    }
  };

  const processCurrentPerson = async () => {
    if (!people[currentIndex]) return;

    setIsProcessing(true);
    setCurrentPerson(people[currentIndex]);
    setPrediction(null);
    setActualEvent(null);
    setComparison(null);
    setAiSuggestions(null);

    try {
      // Step 1: Run algorithm on current person
      const response = await fetch(`${BACKEND_URL}/api/validation/process-person`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          person: people[currentIndex],
          algorithm_version: algorithmVersion,
          applied_corrections: corrections
        })
      });

      const result = await response.json();

      setPrediction(result.prediction);
      setActualEvent(result.actual_event);
      setComparison(result.comparison);
      
      if (!result.comparison.is_match) {
        setAiSuggestions(result.ai_suggestions);
      }

    } catch (error) {
      console.error('Error processing person:', error);
      alert('Error processing person');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleApproveCorrection = async (correction) => {
    // Add correction to the list
    const newCorrections = [...corrections, {
      person: currentPerson.name,
      correction: correction,
      applied_at: currentIndex
    }];
    
    setCorrections(newCorrections);
    
    // Increment algorithm version
    const versionParts = algorithmVersion.split('.');
    versionParts[2] = parseInt(versionParts[2]) + 1;
    setAlgorithmVersion(versionParts.join('.'));
    
    alert(`✅ Correction applied! Algorithm updated to v${versionParts.join('.')}`);
    
    // Move to next person
    moveToNextPerson();
  };

  const handleRejectCorrection = () => {
    alert('❌ Correction rejected. Moving to next person with current algorithm.');
    moveToNextPerson();
  };

  const moveToNextPerson = () => {
    if (currentIndex < people.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setCurrentPerson(null);
      setPrediction(null);
      setActualEvent(null);
      setComparison(null);
      setAiSuggestions(null);
    } else {
      alert('🎉 All people processed! Training complete.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-white text-4xl font-bold mb-2">🧠 Interactive Learning Mode</h1>
          <p className="text-cyan-400 text-lg">Sequential validation with real-time algorithm improvement</p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20 mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-white text-xl font-semibold">Progress</h2>
              <p className="text-white/60">Person {currentIndex + 1} of {people.length}</p>
            </div>
            <div className="text-right">
              <p className="text-cyan-400 text-2xl font-bold">{algorithmVersion}</p>
              <p className="text-white/60 text-sm">Algorithm Version</p>
            </div>
          </div>

          <div className="w-full bg-white/10 rounded-full h-4 overflow-hidden mb-2">
            <div
              className="bg-gradient-to-r from-cyan-500 to-blue-500 h-full transition-all duration-500"
              style={{ width: `${((currentIndex + 1) / people.length) * 100}%` }}
            />
          </div>

          <div className="flex justify-between text-white/60 text-sm">
            <span>{currentIndex} completed</span>
            <span>{people.length - currentIndex} remaining</span>
          </div>
        </div>

        {/* Current Person Card */}
        {!currentPerson && !isProcessing && (
          <div className="bg-white/10 backdrop-blur-xl rounded-xl p-8 border border-white/20 text-center">
            <h2 className="text-white text-2xl font-bold mb-4">
              {currentIndex === 0 ? 'Ready to Start' : 'Next Person Ready'}
            </h2>
            <p className="text-white/80 mb-6">
              {people[currentIndex]?.name} ({people[currentIndex]?.date_of_birth})
            </p>
            <button
              onClick={processCurrentPerson}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-lg rounded-xl transition-all"
            >
              <Play className="w-6 h-6" />
              Process Person {currentIndex + 1}
            </button>
          </div>
        )}

        {/* Processing State */}
        {isProcessing && (
          <div className="bg-white/10 backdrop-blur-xl rounded-xl p-8 border border-white/20 text-center">
            <RefreshCw className="w-16 h-16 text-cyan-400 mx-auto mb-4 animate-spin" />
            <h2 className="text-white text-2xl font-bold mb-2">Processing...</h2>
            <p className="text-white/80">
              Running algorithm v{algorithmVersion} on {people[currentIndex]?.name}
            </p>
          </div>
        )}

        {/* Results Display */}
        {currentPerson && prediction && comparison && (
          <div className="space-y-6">
            {/* Person Info */}
            <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
              <h2 className="text-white text-2xl font-bold mb-4">
                👤 {currentPerson.name}
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-white/60">Birth:</span> <span className="text-white">{currentPerson.date_of_birth}</span></div>
                <div><span className="text-white/60">Time:</span> <span className="text-white">{currentPerson.time_of_birth}</span></div>
                <div><span className="text-white/60">Place:</span> <span className="text-white">{currentPerson.place_of_birth}</span></div>
                <div><span className="text-white/60">Algorithm:</span> <span className="text-cyan-400">v{algorithmVersion}</span></div>
              </div>
            </div>

            {/* Prediction vs Actual */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Predicted */}
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-cyan-400/30">
                <h3 className="text-cyan-400 text-xl font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="w-6 h-6" />
                  Predicted {prediction.multiple_marriages > 1 ? `(${prediction.multiple_marriages} Marriages)` : ''}
                </h3>
                
                {/* Show all marriage predictions if multiple */}
                {prediction.all_timings && prediction.all_timings.length > 1 ? (
                  <div className="space-y-4">
                    {prediction.all_timings.map((timing, idx) => (
                      <div key={idx} className="bg-black/20 rounded p-3 border border-cyan-500/20">
                        <p className="text-cyan-300 font-semibold mb-2">{timing.event_type?.replace('_', ' ').toUpperCase() || `Marriage ${idx + 1}`}</p>
                        <div className="text-white text-sm space-y-1">
                          <p><strong>Years:</strong> {timing.year_range?.start} - {timing.year_range?.end}</p>
                          <p><strong>Age:</strong> {timing.age_range?.start} - {timing.age_range?.end}</p>
                          <p><strong>Confidence:</strong> {(timing.confidence * 100).toFixed(0)}%</p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2 text-white">
                    <p><strong>Year Range:</strong> {prediction.year_range?.start} - {prediction.year_range?.end}</p>
                    <p><strong>Age Range:</strong> {prediction.age_range?.start} - {prediction.age_range?.end}</p>
                    <p><strong>Confidence:</strong> {(prediction.confidence * 100).toFixed(0)}%</p>
                    <p><strong>Category:</strong> {prediction.category}</p>
                  </div>
                )}
                
                <div className="mt-4 bg-black/30 rounded p-3">
                  <p className="text-white/60 text-xs mb-2">Astrological Basis:</p>
                  <p className="text-green-400 text-sm">{prediction.basis}</p>
                </div>
              </div>

              {/* Actual */}
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-purple-400/30">
                <h3 className="text-purple-400 text-xl font-bold mb-4 flex items-center gap-2">
                  <AlertCircle className="w-6 h-6" />
                  Actual Event
                </h3>
                <div className="space-y-2 text-white">
                  <p><strong>Found:</strong> {actualEvent?.found ? '✅ Yes' : '❌ No'}</p>
                  <p><strong>Date:</strong> {actualEvent?.date || 'Not found'}</p>
                  <p><strong>Source:</strong> {actualEvent?.source || 'N/A'}</p>
                </div>
                {actualEvent?.details && (
                  <div className="mt-4 bg-black/30 rounded p-3">
                    <p className="text-white/80 text-sm">{actualEvent.details}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Comparison Result */}
            <div className={`rounded-xl p-6 border-2 ${
              comparison.is_match 
                ? 'bg-green-500/10 border-green-500'
                : 'bg-red-500/10 border-red-500'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                {comparison.is_match ? (
                  <CheckCircle className="w-10 h-10 text-green-400" />
                ) : (
                  <XCircle className="w-10 h-10 text-red-400" />
                )}
                <div>
                  <h3 className="text-white text-2xl font-bold">
                    {comparison.is_match ? 'SUCCESS!' : 'MISMATCH'}
                  </h3>
                  <p className="text-white/80">{comparison.message}</p>
                </div>
              </div>
              {!comparison.is_match && comparison.years_off && (
                <p className="text-red-400 font-semibold">
                  Prediction was {comparison.years_off} years off
                </p>
              )}
            </div>

            {/* AI Suggestions (if mismatch) */}
            {aiSuggestions && aiSuggestions.length > 0 && (
              <div className="bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
                <h3 className="text-white text-xl font-bold mb-4">🤖 AI-Generated Corrections</h3>
                <p className="text-white/60 mb-6">Review these suggestions to improve the algorithm:</p>

                <div className="space-y-4">
                  {aiSuggestions.map((suggestion, idx) => (
                    <div key={idx} className="bg-white/5 rounded-lg p-4 border border-white/10">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="text-cyan-400 font-semibold text-lg mb-2">
                            {idx + 1}. {suggestion.description}
                          </p>
                          <p className="text-white/70 text-sm mb-3">{suggestion.rationale}</p>
                          <div className="bg-black/30 rounded p-3">
                            <p className="text-green-400 text-sm font-mono">{suggestion.implementation}</p>
                          </div>
                        </div>
                        <span className="ml-4 px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm">
                          {(suggestion.confidence * 100).toFixed(0)}%
                        </span>
                      </div>

                      <div className="flex gap-3 mt-4">
                        <button
                          onClick={() => handleApproveCorrection(suggestion)}
                          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Apply This Correction
                        </button>
                        <button
                          onClick={handleRejectCorrection}
                          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={moveToNextPerson}
                  className="w-full mt-6 flex items-center justify-center gap-2 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                >
                  Skip All - Keep Current Algorithm
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* If Match - Move to Next */}
            {comparison.is_match && (
              <button
                onClick={moveToNextPerson}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold text-lg rounded-xl transition-all"
              >
                Continue to Next Person
                <ArrowRight className="w-6 h-6" />
              </button>
            )}

            {/* If no actual event data found - Skip this person */}
            {!comparison.is_match && (!actualEvent || !actualEvent.found) && (!aiSuggestions || aiSuggestions.length === 0) && (
              <button
                onClick={moveToNextPerson}
                className="w-full flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-bold text-lg rounded-xl transition-all"
              >
                Skip - No Event Data Available
                <ArrowRight className="w-6 h-6" />
              </button>
            )}
          </div>
        )}

        {/* Corrections History */}
        {corrections.length > 0 && (
          <div className="mt-8 bg-white/10 backdrop-blur-xl rounded-xl p-6 border border-white/20">
            <h3 className="text-white text-xl font-bold mb-4">📝 Applied Corrections ({corrections.length})</h3>
            <div className="space-y-2">
              {corrections.map((corr, idx) => (
                <div key={idx} className="bg-white/5 rounded p-3 text-sm">
                  <p className="text-cyan-400 font-semibold">{corr.person}</p>
                  <p className="text-white/70">{corr.correction.description}</p>
                  <p className="text-white/40 text-xs">Applied at person #{corr.applied_at + 1}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InteractiveLearning;
